import { liveControlSchema, liveEventSchema, liveResultSchema, type LiveRequest, type LiveResult } from '@/features/llama-cpp-browser/live-types';
import { liveRequestSchema } from './live-protocol';
import { observeMeasurementMemory } from './memory-observation';
import { observeWorkerMemory } from '@/features/llama-cpp-browser/memory-diagnostics-store';
import { audioGenerationResultSchema, audioPreviewEventSchema, type AudioPreviewEvent } from '@/features/audio-generation/types';
import { profileCapabilitiesSchema } from '@/features/llama-cpp-browser/runtime/profile-capabilities';
import { deletionPlanSchema, deletionResultSchema } from '@/features/llama-cpp-browser/runtime/deletion-plan';
import { classifyFailure, diagnosticSchema, dispatchLimitDetails, logDiagnostic, logFailure, type Diagnostic } from '@/features/llama-cpp-browser/debug-log';
import { workerCapability, workerProxy, type WorkerRemote } from '@/utils/worker-transport';
import { errorCode, generationEventSchema, generationResultSchema, LlamaCppBrowserError, modelSchema, modelsSchema, progressSchema, type LocalModel, type Progress } from '@/features/llama-cpp-browser/types';
import { workerAudioCallSchema, workerPrepareCallSchema, workerGenerateCallSchema, type LlamaCppWorkerApi, type LlamaCppWorkerClient } from './types';

// Both transports share cancellation, validation and callback lifetime rules.
export function createLlamaCppWorkerSessionClient({ worker, remote, disposeTransport, getAssetBaseURL }: {
  worker: Worker,
  remote: WorkerRemote<LlamaCppWorkerApi>,
  disposeTransport: ({ active }: { active: boolean }) => void | Promise<void>,
  getAssetBaseURL: () => string | undefined,
}): LlamaCppWorkerClient {
  const stopObservingMemory = observeWorkerMemory({ worker });
  let disposed = false;
  const disposeListeners = new Set<() => void>();
  let lastOperation: Diagnostic | undefined;
  let lastNativeFailure: Diagnostic | undefined;
  const pendingOperations = new Map<Diagnostic['stage'], { diagnostic: Diagnostic, started: number }>();
  let debugEnabled = false;
  let waitingTimer: ReturnType<typeof setTimeout> | undefined;
  const stopWaiting = (): void => {
    if (waitingTimer !== undefined) {
      clearTimeout(waitingTimer);
    }
    waitingTimer = undefined;
  };
  const waitForOperation = ({ diagnostic, started }: { diagnostic: Diagnostic, started: number }): void => {
    if (!debugEnabled) {
      return;
    }
    const report = (): void => {
      if (disposed) {
        return;
      }
      logDiagnostic({ diagnostic: { ...diagnostic, ...lastOperation, stage: diagnostic.stage, event: 'operation-waiting', lastStage: lastOperation?.stage, lastEvent: lastOperation?.event, elapsedMs: performance.now() - started } });
      waitingTimer = setTimeout(report, 30000);
    };
    waitingTimer = setTimeout(report, 15000);
  };
  const recordOperation = ({ diagnostic }: { diagnostic: Diagnostic & { event: 'operation-start' | 'operation-complete' } }): void => {
    stopWaiting();
    lastOperation = diagnostic;
    switch (diagnostic.event) {
    case 'operation-complete': {
      pendingOperations.delete(diagnostic.stage);
      // A native completion can leave its enclosing helper still running.
      const pending = Array.from(pendingOperations.values()).at(-1);
      if (pending) {
        waitForOperation(pending);
      }
      return;
    }
    case 'operation-start': {
      const operation = { diagnostic, started: performance.now() };
      pendingOperations.set(diagnostic.stage, operation);
      waitForOperation(operation);
      return;
    }
    default: {
      const exhaustive: never = diagnostic.event;
      throw new Error(`Unknown operation event: ${exhaustive}`);
    }
    }
  };
  let nextGenerationId = 0;
  let rejectActive: (() => void) | undefined;
  let disposal: Promise<void> | undefined;
  const dispose = (): Promise<void> => {
    if (disposal) {
      return disposal;
    }
    const completion = Promise.withResolvers<void>();
    disposal = completion.promise;
    stopObservingMemory();
    disposed = true;
    debugEnabled = false;
    stopWaiting();
    pendingOperations.clear();
    const active = rejectActive !== undefined;
    rejectActive?.();
    worker.removeEventListener('error', onError);
    worker.removeEventListener('messageerror', onMessageError);
    // Preserve synchronous hosted termination; expose completion of bounded
    // standalone teardown so a measured model cannot overlap the next Worker.
    try {
      void Promise.resolve(disposeTransport({ active })).catch(error => {
        logFailure({ stage: 'cleanup', error });
      }).finally(completion.resolve);
    } catch (error) {
      logFailure({ stage: 'cleanup', error });
      completion.resolve();
    }
    for (const listener of disposeListeners) {
      try {
        listener();
      } catch { /* Disposal observers cannot interrupt cleanup. */ }
    }
    disposeListeners.clear();
    return disposal;
  };
  // eslint-disable-next-line local-rules-named-args/require-named-args -- DOM Worker error listener signature.
  const onError = (event: ErrorEvent): void => {
    const failureKind = lastNativeFailure?.failureKind ?? classifyFailure({ error: event.error instanceof Error ? event.error : event.message });
    logDiagnostic({
      diagnostic: {
        ...lastOperation,
        ...dispatchLimitDetails({ message: event.message }),
        ...lastNativeFailure,
        event: 'failed',
        stage: 'worker-error',
        failureKind,
        lastStage: lastOperation?.stage,
        lastEvent: lastOperation?.event,
      },
    });
    event.preventDefault();
    dispose();
  };
  const onMessageError = (): void => {
    logDiagnostic({ diagnostic: { ...lastOperation, event: 'failed', stage: 'worker-messageerror', lastStage: lastOperation?.stage, lastEvent: lastOperation?.event } });
    dispose();
  };
  worker.addEventListener('error', onError);
  worker.addEventListener('messageerror', onMessageError);
  async function invoke<T>({ call, signal, onAbort, abortTimeoutMs }: { call: () => Promise<T>, signal: AbortSignal | undefined, onAbort: (() => void) | undefined, abortTimeoutMs: number | undefined }): Promise<T> {
    if (signal?.aborted) {
      throw new LlamaCppBrowserError({ code: 'aborted' });
    }
    if (disposed) {
      throw new LlamaCppBrowserError({ code: 'worker-failed' });
    }
    if (rejectActive) {
      throw new LlamaCppBrowserError({ code: 'busy' });
    }
    stopWaiting();
    pendingOperations.clear();
    lastOperation = undefined;
    lastNativeFailure = undefined;
    let abortTimer: ReturnType<typeof setTimeout> | undefined;
    const abort = (): void => {
      if (!onAbort) {
        dispose();
        return;
      }
      // Native generation needs a bounded escape hatch. Local imports use async
      // streams and must be allowed to finish rollback, even on slow storage:
      // terminating them here leaves hidden partial directories that block retry.
      if (abortTimeoutMs !== undefined) {
        abortTimer = setTimeout(dispose, abortTimeoutMs);
      }
      onAbort();
    };
    signal?.addEventListener('abort', abort, { once: true });
    try {
      const result = await new Promise<T>((resolve, reject) => {
        rejectActive = () => reject(new LlamaCppBrowserError({ code: signal?.aborted ? 'aborted' : 'worker-failed' }));
        void call().then(resolve, reject);
      });
      if (signal?.aborted) {
        throw new LlamaCppBrowserError({ code: 'aborted' });
      }
      return result;
    } catch (error) {
      if (!disposed) {
        logFailure({ stage: 'worker-rpc', error });
      }
      const code = errorCode({ error });
      if (signal?.aborted && (code === 'runtime-error' || code === 'worker-failed')) {
        dispose();
      }
      throw new LlamaCppBrowserError({ code: signal?.aborted ? 'aborted' : errorCode({ error }) });
    } finally {
      stopWaiting();
      debugEnabled = false;
      pendingOperations.clear();
      if (abortTimer !== undefined) {
        clearTimeout(abortTimer);
      }
      signal?.removeEventListener('abort', abort);
      rejectActive = undefined;
    }
  }
  async function importWithCancellation({ call, onProgress, signal }: {
    call: ({ generationId, report }: { generationId: number, report: Parameters<LlamaCppWorkerApi['importModel']>[1] }) => Promise<LocalModel>,
    onProgress: ({ progress }: { progress: Progress }) => void,
    signal: AbortSignal | undefined,
  }): Promise<LocalModel> {
    const generationId = ++nextGenerationId;
    let acceptingProgress = true;
    try {
      return modelSchema.parse(await invoke({
        call: () => call({
          generationId,
          report: workerProxy({
            // eslint-disable-next-line local-rules-named-args/require-named-args -- This proxy receives a Progress value as a top-level Comlink argument.
            value: (progress: Progress) => {
              if (acceptingProgress && !disposed && !signal?.aborted) {
                onProgress({ progress: progressSchema.parse(progress) });
              }
            },
          }),
        }),
        signal,
        onAbort: () => {
          void remote.cancelGeneration({ generationId }).catch(dispose);
        },
        abortTimeoutMs: undefined,
      }));
    } finally {
      acceptingProgress = false;
    }
  }
  return {
    async live({ request, onEvent, onProgress, signal }) {
      let accepted: LiveRequest;
      switch (request.type) {
      case 'open': accepted = liveRequestSchema.parse({ ...request, prepare: { ...request.prepare, assetBaseURL: getAssetBaseURL() } });
        break;
      case 'command': accepted = liveRequestSchema.parse(request);
        break;
      default: {
        const exhaustive: never = request;
        throw new Error(String(exhaustive));
      }
      }
      const generationId = ++nextGenerationId;
      let accepting = true;
      try {
        return liveResultSchema.parse(await invoke<LiveResult>({
          call: () => remote.live(workerCapability({ value: { request: accepted, generationId }, capability: 'file-system-handle-clone' }), workerProxy({
            // eslint-disable-next-line local-rules-named-args/require-named-args -- Direct Comlink event proxy.
            value: async (event: unknown) => {
              if (accepting && !disposed && !signal?.aborted) {
                await onEvent({ event: liveEventSchema.parse(event) });
              }
            },
          }), workerProxy({
            // eslint-disable-next-line local-rules-named-args/require-named-args -- Direct Comlink progress proxy.
            value: (progress: unknown) => {
              if (accepting && !disposed && !signal?.aborted) {
                onProgress({ progress: progressSchema.parse(progress) });
              }
            },
          })),
          signal,
          onAbort: () => {
            void remote.cancelGeneration({ generationId }).catch(dispose);
          },
          abortTimeoutMs: 5000,
        }));
      } finally {
        accepting = false;
      }
    },
    async interruptLive({ sessionId, turnId }) {
      if (disposed) {
        throw new LlamaCppBrowserError({ code: 'worker-failed' });
      }
      // Bypass invoke: the response owns its admission gate until it stops.
      await remote.interruptLive(liveControlSchema.parse({ sessionId, turnId }));
    },
    subscribeDisposed({ listener }) {
      if (disposed) {
        listener();
        return () => {};
      }
      disposeListeners.add(listener);
      return () => {
        disposeListeners.delete(listener);
      };
    },
    async releaseRuntime({ signal }) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) {
        abort();
      }
      // Release is serialized with inference. A wedged cleanup retires the
      // transport instead of handing a still-active runtime to another model.
      const timer = setTimeout(abort, 10000);
      try {
        await invoke({ call: () => remote.release(), signal: controller.signal, onAbort: undefined, abortTimeoutMs: undefined });
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
      }
    },
    probeProfiles: async ({ signal }) => profileCapabilitiesSchema.parse(await invoke({ call: () => remote.probeProfiles(), signal, onAbort: undefined, abortTimeoutMs: undefined })),
    listModelEntries: async ({ signal }) => modelsSchema.parse(await invoke({ call: () => remote.listModelEntries(), signal, onAbort: undefined, abortTimeoutMs: undefined })),
    importModel: ({ file, onProgress, signal }) => importWithCancellation({
      signal,
      onProgress,
      call: ({ generationId, report }) => remote.importModel({ file, generationId }, report),
    }),
    importDirectory: ({ directory, onProgress, signal }) => importWithCancellation({
      signal,
      onProgress,
      call: ({ generationId, report }) => remote.importDirectory({ directory, generationId }, report),
    }),
    removeModel: async ({ plan, signal }) => {
      return deletionResultSchema.parse(await invoke({ call: () => remote.removeModel(workerCapability({ value: { plan: deletionPlanSchema.parse(plan) }, capability: 'file-system-handle-clone' })), signal, onAbort: undefined, abortTimeoutMs: undefined }));
    },
    prepareModel: async ({ request, onProgress, signal }) => {
      const accepted = workerPrepareCallSchema.parse({ ...request, generationId: ++nextGenerationId, assetBaseURL: getAssetBaseURL() });
      let acceptingEvents = true;
      try {
        await invoke({
          call: () => remote.prepareModel(workerCapability({ value: accepted, capability: 'file-system-handle-clone' }), workerProxy({
            value: ({ ...event }) => {
              if (acceptingEvents && !disposed && !signal?.aborted) {
                onProgress({ progress: progressSchema.parse(event) });
              }
            },
          })),
          signal,
          onAbort: () => {
            void remote.cancelGeneration({ generationId: accepted.generationId }).catch(dispose);
          },
          abortTimeoutMs: 5000,
        });
      } finally {
        acceptingEvents = false;
      }
    },
    generate: async ({ request, onEvent, onProgress, signal, onSummary, onMemoryDiagnostics, onVisionAdmission }) => {
      const accepted = workerGenerateCallSchema.parse({
        ...request,
        generationId: ++nextGenerationId,
        assetBaseURL: getAssetBaseURL(),
      });
      let acceptingEvents = true;
      const started = accepted.measurement ? performance.now() : undefined;
      const memory = started === undefined ? undefined : observeMeasurementMemory({ worker, now: () => performance.now() - started });
      let admissionFailure: ReturnType<typeof errorCode> | undefined;
      try {
        const result = await invoke({
          call: async () => {
            const imageObservation = accepted.measurement?.observation;
            switch (imageObservation) {
            case 'vision': {
              const { visionAdmissionSchema } = await import('@/features/llama-cpp-browser/performance/vision-admission-schema');
              signal?.throwIfAborted();
              const admission = visionAdmissionSchema.parse(await remote.prepareVision(workerCapability({
                value: {
                  modelId: accepted.modelId,
                  resolvedModel: accepted.resolvedModel,
                  options: accepted.options,
                  debug: accepted.debug,
                  assetBaseURL: accepted.assetBaseURL,
                  generationId: accepted.generationId,
                },
                capability: 'file-system-handle-clone',
              }), workerProxy({
                // eslint-disable-next-line local-rules-named-args/require-named-args -- Direct Comlink progress proxy.
                value: (progress: Progress) => {
                  if (acceptingEvents && !disposed && !signal?.aborted) {
                    onProgress({ progress: progressSchema.parse(progress) });
                  }
                },
              })));
              onVisionAdmission?.({ admission });
              signal?.throwIfAborted();
              switch (admission.status) {
              case 'ready': break;
              case 'skipped': throw new LlamaCppBrowserError({ code: 'unsupported-input' });
              case 'failed':
                admissionFailure = admission.error;
                // Even a native trap must retire the Worker before the next model.
                void dispose();
                throw new LlamaCppBrowserError({ code: admission.error });
              default: {
                const exhaustive: never = admission;
                throw new Error(String(exhaustive));
              }
              }
              break;
            }
            case 'placement': case undefined: break;
            default: {
              const exhaustive: never = imageObservation;
              throw new Error(String(exhaustive));
            }
            }
            return remote.generate(workerCapability({ value: accepted, capability: 'file-system-handle-clone' }),
              workerProxy({
                value: async ({ event }) => {
                  if (acceptingEvents && !disposed) {
                    await onEvent({ event: generationEventSchema.parse(event) });
                  }
                },
              }),
              workerProxy({
                value: ({ ...event }) => {
                  if (acceptingEvents && !disposed && !signal?.aborted) {
                    onProgress({ progress: progressSchema.parse(event) });
                  }
                },
              }),
              workerProxy({
                value: ({ diagnostic }: { diagnostic: unknown }) => {
                  if (!acceptingEvents || disposed) {
                    return;
                  }
                  // Measurement history is collected only from the ordered Worker
                  // notification endpoint, never twice through this proxy callback.
                  if (signal?.aborted) {
                    return;
                  }
                  debugEnabled = accepted.debug === 'on';
                  const checkpoint = diagnosticSchema.parse(diagnostic);
                  if (checkpoint.event === 'operation-start' || checkpoint.event === 'operation-complete') {
                    recordOperation({ diagnostic: { ...checkpoint, event: checkpoint.event } });
                  }
                  if (checkpoint.event === 'native-info' && checkpoint.nativeOperation !== undefined) {
                    lastOperation = { ...lastOperation, ...checkpoint };
                  }
                  if (checkpoint.event === 'native-node-start' || checkpoint.event === 'native-node-complete') {
                    lastOperation = checkpoint;
                  }
                  if (checkpoint.event === 'native-error' && (!lastNativeFailure || checkpoint.failureKind === 'webgpu-dispatch-limit')) {
                    lastNativeFailure = checkpoint;
                  }
                },
              }),
              workerProxy({
                value: ({ diagnostic }: { diagnostic: unknown }) => {
                // A cooperative Stop still owns its terminal summary. Late callbacks
                // after this call has settled must never reach the next trial.
                  if (!acceptingEvents || disposed || !accepted.measurement) {
                    return;
                  }
                  const summary = diagnosticSchema.parse(diagnostic);
                  if (summary.event !== 'generation-performance' || !summary.performance) {
                    throw new Error('Invalid generation summary');
                  }
                  onSummary?.({ diagnostic: summary });
                },
              }));
          },
          signal,
          onAbort: () => {
            void remote.cancelGeneration({ generationId: accepted.generationId }).catch(dispose);
          },
          abortTimeoutMs: 5000,
        });
        return generationResultSchema.parse(result);
      } catch (error) {
        // dispose rejects the active transport with worker-failed; retain this owner's native cause.
        if (admissionFailure !== undefined && !signal?.aborted) {
          throw new LlamaCppBrowserError({ code: admissionFailure });
        }
        throw error;
      } finally {
        acceptingEvents = false;
        const observed = memory?.finish();
        try {
          if (observed) {
            onMemoryDiagnostics?.({ memory: observed });
          }
        } catch { /* Observation must not replace inference success or failure. */ }
      }
    },
    generateAudio: async ({ request, onProgress, cancellationSignal, completionSignal, preview }) => {
      const accepted = workerAudioCallSchema.parse({ ...request, generationId: ++nextGenerationId, assetBaseURL: getAssetBaseURL() });
      let acceptingEvents = true;
      let started = false;
      const finish = (): void => {
        if (started && acceptingEvents && !disposed && !cancellationSignal?.aborted) {
          void remote.finishAudioGeneration({ generationId: accepted.generationId }).catch(() => {
            if (acceptingEvents && !disposed) {
              dispose();
            }
          });
        }
      };
      // The completion signal requests a normal partial result. It never arms the abort
      // timeout, terminates the Worker, or reaches the native abort callback.
      completionSignal?.addEventListener('abort', finish, { once: true });
      let sentVersion = 0;
      let deliveredVersion = 0;
      const requestPreview = (): void => {
        if (!started || !acceptingEvents || disposed || cancellationSignal?.aborted || completionSignal?.aborted || !preview) {
          return;
        }
        const requestVersion = preview.requests.version;
        if (requestVersion <= sentVersion) {
          return;
        }
        sentVersion = requestVersion;
        void remote.requestAudioPreview({ generationId: accepted.generationId, requestVersion }).catch(() => {
          // A late failure for a completed request must not dispose a new owner.
          if (acceptingEvents && !disposed) {
            dispose();
          }
        });
      };
      const unsubscribePreview = preview?.requests.subscribe({ listener: requestPreview });
      try {
        const result = await invoke({
          call: () => {
            const pending = remote.generateAudio(workerCapability({ value: accepted, capability: 'file-system-handle-clone' }),
              workerProxy({
                value: ({ ...event }) => {
                  if (acceptingEvents && !disposed && !cancellationSignal?.aborted) {
                    onProgress({ progress: progressSchema.parse(event) });
                  }
                },
              }),
              workerProxy({
                value: ({ diagnostic }: { diagnostic: unknown }) => {
                  if (!acceptingEvents || disposed || cancellationSignal?.aborted) {
                    return;
                  }
                  debugEnabled = accepted.debug === 'on';
                  const checkpoint = diagnosticSchema.parse(diagnostic);
                  if (checkpoint.event === 'operation-start' || checkpoint.event === 'operation-complete') {
                    recordOperation({ diagnostic: { ...checkpoint, event: checkpoint.event } });
                  }
                  if (checkpoint.event === 'native-info' && checkpoint.nativeOperation !== undefined) {
                    lastOperation = { ...lastOperation, ...checkpoint };
                  }
                  if (checkpoint.event === 'native-node-start' || checkpoint.event === 'native-node-complete') {
                    lastOperation = checkpoint;
                  }
                  if (checkpoint.event === 'native-error' && (!lastNativeFailure || checkpoint.failureKind === 'webgpu-dispatch-limit')) {
                    lastNativeFailure = checkpoint;
                  }
                },
              }), preview ? workerProxy({
                // eslint-disable-next-line local-rules-named-args/require-named-args -- This proxy receives an AudioPreviewEvent as a top-level Comlink argument.
                value: async (event: AudioPreviewEvent) => {
                  if (!acceptingEvents || disposed || cancellationSignal?.aborted) {
                    return;
                  }
                  const acceptedEvent = audioPreviewEventSchema.parse(event);
                  if (acceptedEvent.requestVersion <= deliveredVersion) {
                    return;
                  }
                  if (acceptedEvent.requestVersion > sentVersion) {
                    throw new LlamaCppBrowserError({ code: 'worker-failed' });
                  }
                  deliveredVersion = acceptedEvent.requestVersion;
                  await preview.onPreview({ event: acceptedEvent });
                },
              }) : undefined);
            started = true;
            if (completionSignal?.aborted) {
              finish();
            }
            requestPreview();
            return pending;
          },
          signal: cancellationSignal,
          onAbort: () => {
            void remote.cancelGeneration({ generationId: accepted.generationId }).catch(dispose);
          },
          abortTimeoutMs: 5000,
        });
        return audioGenerationResultSchema.parse(result);
      } finally {
        acceptingEvents = false;
        completionSignal?.removeEventListener('abort', finish);
        unsubscribePreview?.();
      }
    },
    canReuse: () => !disposed,
    dispose,
  };
}

export const TEST_ONLY = {
};
